
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useEffect } from "react";

interface BraceletRegistrationDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  nickname: string;
  setNickname: (nickname: string) => void;
  handleRegister: () => void;
  isRegistering: boolean;
  churchMode?: boolean;
  churchCode?: string;
  setChurchCode?: (code: string) => void;
  churchName?: string;
  setChurchName?: (name: string) => void;
  registrationType?: string;
  setRegistrationType?: (type: string) => void;
  quantity?: number;
  setQuantity?: (quantity: number) => void;
  prefilledCodes?: string[];
}

export function BraceletRegistrationDialog({
  open,
  setOpen,
  nickname,
  setNickname,
  handleRegister,
  isRegistering,
  churchMode = false,
  churchCode = "",
  setChurchCode = () => {},
  churchName = "",
  setChurchName = () => {},
  registrationType = "individual",
  setRegistrationType = () => {},
  quantity = 1,
  setQuantity = () => {},
  prefilledCodes = []
}: BraceletRegistrationDialogProps) {
  // If there are prefilled codes, set them as the default
  useEffect(() => {
    if (churchMode && prefilledCodes && prefilledCodes.length > 0) {
      // Set default values for TEST CONGREGATIONAL
      if (churchCode !== "001") {
        setChurchCode("001");
      }
      if (!churchName) {
        setChurchName("TEST CONGREGATIONAL");
      }
    }
  }, [churchMode, prefilledCodes, churchCode, churchName, setChurchCode, setChurchName]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{churchMode ? "Register Church Bracelets" : "Register Your Product"}</DialogTitle>
          <DialogDescription>
            {churchMode 
              ? "Register bracelets for your church and assign a church code." 
              : "Give your product a nickname to help you identify it later."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {churchMode ? (
            <>
              <div>
                <p className="text-sm font-medium mb-2">Church Code: {churchCode}</p>
                <p className="text-xs text-gray-500 mb-4">
                  This is the unique identifier for your church.
                </p>
              </div>
              <Input
                placeholder="Church Name"
                value={churchName}
                onChange={(e) => setChurchName(e.target.value)}
                className="h-12"
              />
              <Select value={registrationType} onValueChange={setRegistrationType}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Registration Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual Bracelets</SelectItem>
                  <SelectItem value="bulk">Bulk Registration</SelectItem>
                  <SelectItem value="prefilled">Prefilled Codes</SelectItem>
                </SelectContent>
              </Select>
              {registrationType === "bulk" && (
                <Input
                  type="number"
                  placeholder="Quantity"
                  value={quantity.toString()}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  min={1}
                  max={1000}
                  className="h-12"
                />
              )}
              {registrationType === "prefilled" && prefilledCodes.length > 0 && (
                <div className="border rounded p-3 max-h-32 overflow-y-auto">
                  <p className="text-sm font-medium mb-2">Using these codes:</p>
                  <ul className="text-sm space-y-1">
                    {prefilledCodes.map((code, index) => (
                      <li key={index} className="text-gray-600">{code}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            <Input
              placeholder="Enter a nickname (e.g. My Grace Bracelet)"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="h-12"
            />
          )}
          <Button 
            className="w-full h-12 text-lg font-medium" 
            onClick={handleRegister}
            disabled={isRegistering || (churchMode ? !churchName.trim() : !nickname.trim())}
          >
            {isRegistering ? "Registering..." : (churchMode ? "Register Church Bracelets" : "Register Product")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
